import React, { FC, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import Toggle from 'components/toggle';
import { useSetLoyaltyCardRewardsTierMutation } from 'generated/rbi-graphql';
import { useBankMyRewardsTierId, useSelectedRewardTier } from 'hooks/loyalty/use-selected-reward';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useThLoyaltyContext } from 'state/loyalty';
import { ILoyaltyCategory } from 'state/loyalty/types';

import { RewardTier } from './reward-tier';
import { RewardsManagementSkeleton } from './rewards-management.skeleton';
import { Container, SubTitle, Title, TitleContainer } from './rewards-management.styled';

type LoyaltyCategoryOption = ILoyaltyCategory['options'][number];

/**
 * @name RewardsManagement
 * @purpose handles the TH Loyalty Rewards Tier selection UX
 * This component is the entry point for the Toggle UX.
 */

const RewardsManagement: FC = ({ ...props }) => {
  const enableTHRewardsTierToggleUI = useFlag(LaunchDarklyFlag.ENABLE_TH_REWARD_TIER_TOGGLE_UI);
  const { formatMessage } = useIntl();

  const [loyaltyCategories, setLoyaltyCategories] = useState<ILoyaltyCategory[]>([]);

  const { getLoyaltyCategory } = useThLoyaltyContext();
  const { bankMyRewardTierId } = useBankMyRewardsTierId();

  useEffect(() => {
    getLoyaltyCategory().then((availableLoyaltyCategories: Array<ILoyaltyCategory> | null) =>
      setLoyaltyCategories(availableLoyaltyCategories || [])
    );
  }, [getLoyaltyCategory, setLoyaltyCategories]);

  const onSetRewardsTierError = () => {};

  const [setLoyaltyCardRewardsTier] = useSetLoyaltyCardRewardsTierMutation();
  const { selectedRewardTierId, onChangeRewardTier } = useSelectedRewardTier(
    loyaltyCategories,
    setLoyaltyCardRewardsTier,
    { onSetRewardsTierError }
  );

  const selectedTier = useMemo(() => {
    const getLoyaltyCategories = (rewardTierId: string) => {
      return loyaltyCategories.reduce(
        (acc: { [key: string]: LoyaltyCategoryOption }, { options }) => {
          options.forEach(val => (acc[val._id] = val));
          return acc;
        },
        {}
      )[rewardTierId];
    };
    return selectedRewardTierId ? getLoyaltyCategories(selectedRewardTierId) : undefined;
  }, [selectedRewardTierId, loyaltyCategories]);

  const [redeemPoints, setRedeemPoints] = useState<boolean>();

  const handleToggle = (e: any) => {
    const { checked } = e.target;
    setRedeemPoints(checked);
    if (checked) {
      onChangeRewardTier('ON');
    } else {
      onChangeRewardTier(bankMyRewardTierId);
    }
  };

  useEffect(() => {
    if (redeemPoints === undefined && selectedRewardTierId != null && bankMyRewardTierId != null) {
      setRedeemPoints(selectedRewardTierId !== bankMyRewardTierId);
    }
  }, [selectedRewardTierId, bankMyRewardTierId, redeemPoints]);

  /**
   * NOTE: This UI is being rolled out slowly behind this flag
   * @TODO: remove this check once the flag is fully rolled out.
   */
  if (!enableTHRewardsTierToggleUI) {
    return null;
  }

  return (
    <Container {...props}>
      <TitleContainer>
        <Title>{formatMessage({ id: 'redeemMyPoints' })}</Title>
        {typeof redeemPoints === 'boolean' ? (
          <Toggle
            checked={redeemPoints}
            onChange={handleToggle}
            data-testid="rewards-management-toggle"
          />
        ) : (
          <RewardsManagementSkeleton />
        )}
      </TitleContainer>
      {redeemPoints === false ? (
        <SubTitle data-testid="banking-points">{formatMessage({ id: 'bankingPoints' })}</SubTitle>
      ) : (
        <RewardTier selectedTier={selectedTier} data-testid="reward-tier" />
      )}
    </Container>
  );
};

export default RewardsManagement;
